import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    ReactNode
} from 'react';
import {
    TFilterField,
    IFilter,
    IInitialFilter,
    TFilterValue,
    IFilterTexts,
    IFilterCallbacks,
    IFilterBaseProps
} from './HBFilters.types';

const DEFAULT_TEXTS: Required<IFilterTexts> = {
    removeAllFilters: 'Remove all filters',
    addFilter: 'Add filter',
    selectFilter: 'Select filter:',
    selectOption: 'Select option:',
    selectOptions: 'Select options:',
    clearSelection: 'Clear selection',
    deleteFilter: 'Delete filter'
};

/**
 * The value provided by the FiltersContext, containing all filter state.
 */
interface IFiltersContext extends IFilterBaseProps, IFilterCallbacks {
    activeFilters: IFilter[]; // The current filters array
    filtersById: Record<string, IFilter>; // Quick lookup map of filters by ID
    setFilters: (filters: IInitialFilter[]) => void; // Set the entire filters array
}

const isFilterActive = (value: TFilterValue): boolean => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return !!value;
};

const enrichFilters = (
    filters: IInitialFilter[],
    availableFields: TFilterField[]
): IFilter[] => {
    const fieldsById = Object.fromEntries(availableFields.map(f => [f.id, f]));

    return filters
        .map(filter => {
            const field = fieldsById[filter.id];
            if (!field) {
                return null;
            }
            return {
                ...filter,
                field,
                isActive: isFilterActive(filter.value)
            };
        })
        .filter((filter): filter is IFilter => filter !== null);
};

// Create the context with an undefined default value
const FiltersContext = createContext<IFiltersContext | undefined>(undefined);

/**
 * Custom hook to access the FiltersContext value.
 * Throws if used outside a FiltersProvider.
 */
export const useFiltersContext = () => {
    const ctx = useContext(FiltersContext);
    if (!ctx) {
        throw new Error(
            'useFiltersContext must be used within FiltersProvider'
        );
    }
    return ctx;
};

/**
 * Props for the FiltersProvider component.
 * Extends the main filter component props and adds children.
 */
interface IFiltersProviderProps extends IFilterBaseProps, IFilterCallbacks {
    children: ReactNode;
    availableFields: TFilterField[];
    initialFilters?: IInitialFilter[];
    onChange: (filters: IInitialFilter[]) => void;
    onAddFilter?: (filter: IInitialFilter) => void;
    onDeleteFilter?: (filter: IInitialFilter) => void;
    onFilterActivated?: (filter: IInitialFilter) => void;
    onRemoveAll?: () => void;
    texts?: IFilterTexts;
}

/**
 * FiltersProvider manages all filter state and provides it via context.
 * It wraps the filter UI and synchronizes state with the parent via onChange.
 */
export const FiltersProvider = ({
    children,
    availableFields,
    initialFilters = [],
    onChange,
    onAddFilter,
    onDeleteFilter,
    onFilterActivated,
    onRemoveAll,
    texts = {}
}: IFiltersProviderProps) => {
    // State for the array of filters
    const [filters, setFiltersState] =
        useState<IInitialFilter[]>(initialFilters);

    // Use effect to handle parent synchronization
    const setFilters = useCallback((newFilters: IInitialFilter[]) => {
        setFiltersState(newFilters);
    }, []);

    const enrichedFilters = enrichFilters(filters, availableFields);

    // Create memoized maps for quick lookups
    const filtersById = useMemo(
        () => Object.fromEntries(enrichedFilters.map(f => [f.id, f])),
        [enrichedFilters]
    );

    // Merge custom texts with defaults
    const mergedTexts = useMemo(
        () => ({
            ...DEFAULT_TEXTS,
            ...texts
        }),
        [texts]
    );

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(
        () => ({
            availableFields,
            activeFilters: enrichedFilters,
            filtersById,
            setFilters,
            onChange,
            onAddFilter,
            onDeleteFilter,
            onFilterActivated,
            onRemoveAll,
            texts: mergedTexts
        }),
        [
            availableFields,
            enrichedFilters,
            filtersById,
            setFilters,
            onChange,
            onAddFilter,
            onDeleteFilter,
            onFilterActivated,
            onRemoveAll,
            mergedTexts
        ]
    );

    return (
        <FiltersContext.Provider value={value}>
            {children}
        </FiltersContext.Provider>
    );
};
